import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecentHeadlines, useNewsStore } from '../store/newsStore';
import './NewsTicker.css';

export const NewsTicker: React.FC = () => {
  const headlines = useRecentHeadlines();
  const toggleOverlay = useNewsStore((state) => state.toggleOverlay);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Cycle through headlines: newest first (index 0), then older
  useEffect(() => {
    if (headlines.length <= 1 || isPaused) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % headlines.length);
    }, 8000); // 8 seconds per headline for readability

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [headlines.length, isPaused]);

  // Reset to newest (index 0) if out of bounds
  useEffect(() => {
    if (headlines.length > 0 && currentIndex >= headlines.length) {
      setCurrentIndex(0);
    }
  }, [headlines.length, currentIndex]);

  const formatTime = (timestamp: string | number | undefined) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleTickerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
    setIsPaused(!isExpanded);
  };

  const handleLabelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    toggleOverlay();
  };

  // Close on outside click or escape
  useEffect(() => {
    if (!isExpanded) return;

    const handleClose = (e: MouseEvent | KeyboardEvent) => {
      if (e.type === 'keydown' && (e as KeyboardEvent).key !== 'Escape') return;
      if (e.type === 'click' && (e.target as HTMLElement).closest('.news-ticker')) return;
      setIsExpanded(false);
      setIsPaused(false);
    };

    document.addEventListener('click', handleClose);
    document.addEventListener('keydown', handleClose);
    return () => {
      document.removeEventListener('click', handleClose);
      document.removeEventListener('keydown', handleClose);
    };
  }, [isExpanded]);

  if (headlines.length === 0) {
    return (
      <div className="news-ticker" onClick={handleLabelClick}>
        <div className="news-ticker-label">
          <span className="ticker-icon">📰</span>
          <span>NEWS</span>
        </div>
        <div className="news-ticker-content">
          <span className="ticker-empty">No news available</span>
        </div>
      </div>
    );
  }

  const currentHeadline = headlines[currentIndex];
  const importance = currentHeadline?.importance || 'medium';
  const isBreaking = importance === 'high' || currentHeadline?.category === 'BREAKING';

  return (
    <div className={`news-ticker ${isExpanded ? 'expanded' : ''}`}>
      <div className="news-ticker-label" onClick={handleLabelClick}>
        <span className="ticker-icon">📰</span>
        <span className="ticker-label-text">NEWS</span>
        {/* Time shown in label on mobile for more headline space */}
        <span className="ticker-time-label">{formatTime(currentHeadline?.timestamp)}</span>
      </div>

      <div className="news-ticker-content" onClick={handleTickerClick}>
        {isExpanded ? (
          <div className="ticker-expanded-view">
            <span className="ticker-time-inline">{formatTime(currentHeadline?.timestamp)}</span>
            {currentHeadline?.source === 'x' && (
              <span className="ticker-x-tag">X {currentHeadline.category}</span>
            )}
            {isBreaking && <span className="ticker-breaking-tag">BREAKING</span>}
            <span className={`ticker-headline-full importance-${importance}`}>
              {currentHeadline?.headline}
            </span>
            <span className="ticker-tap-hint">Tap to close</span>
          </div>
        ) : (
          <div className="ticker-slide-container">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="ticker-headline-row"
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: -20, opacity: 0 }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              >
                <span className="ticker-time-inline">{formatTime(currentHeadline?.timestamp)}</span>
                {/* Breaking indicator - full tag on desktop, pulsing dot on mobile */}
                {currentHeadline?.source === 'x' && (
                  <span className="ticker-x-tag">X {currentHeadline.category}</span>
                )}
                {isBreaking && (
                  <>
                    <span className="ticker-breaking-tag">BREAKING</span>
                    <span className="ticker-breaking-dot" title="Breaking News" />
                  </>
                )}
                <span className={`ticker-headline importance-${importance}`}>
                  {currentHeadline?.headline}
                </span>
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      <div className="news-ticker-meta">
        {isPaused && <span className="ticker-paused">⏸</span>}
        <span className="ticker-count">{currentIndex + 1}/{headlines.length}</span>
      </div>
    </div>
  );
};

export default NewsTicker;
